import { useCallback, useEffect, useState } from 'react';
import { adminApi as defaultAdminApi } from './admin-api';
import { PlanForm } from './PlanForm';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { formatMoney } from '../lib/format';
import { PageState } from '../components/PageState';
import { ConfirmationSheet } from '../components/ConfirmationSheet';
import { useFocusTrap } from '../components/useFocusTrap';
import './admin.css';

const TITLE = 'Planes';
const EMPTY_VALUES = { name: '', price: '' };

// Verbatim from the design spec's "Planes" paragraph: changing a price never
// rewrites periods already created (each Membership keeps its own
// amount/name snapshot), and deactivating only blocks NEW periods — it never
// deletes what already happened.
const EXPLANATION = 'Cambiar el precio de un plan no modifica períodos anteriores: cada cuota ya generada conserva su propio importe. Desactivar un plan impide abrir períodos nuevos con él, pero no borra su historial.';

function PlanSheet({ children }) {
  const sheetRef = useFocusTrap(true);
  return <div className="gym-admin-sheet-backdrop"><div ref={sheetRef} tabIndex={-1} className="gym-card gym-admin-sheet">{children}</div></div>;
}

function toEditValues(plan) {
  return { name: plan.name, price: String(plan.price) };
}

/**
 * `/admin/planes` (spec "Administrador"/"Planes"): lists the whole catalog
 * from `GET /plans` (admin sees active and inactive), creates plans, edits
 * name/price inline per row, and toggles `isActive` — deactivating asks for
 * confirmation first since it changes what new periods can be opened,
 * reactivating does not since it only undoes that restriction.
 *
 * Every mutation reloads the catalog from a fresh `GET /plans` afterwards
 * (never appends/patches the list locally), so what's on screen always
 * matches what the server just persisted.
 */
export function PlansPage({ api = defaultAdminApi }) {
  const { handleApiError } = useAuth();
  const [state, setState] = useState({ status: 'loading', plans: null, message: '' });

  const [createValues, setCreateValues] = useState(EMPTY_VALUES);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState(EMPTY_VALUES);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const [confirmDeactivateId, setConfirmDeactivateId] = useState(null);
  const [actionSubmittingId, setActionSubmittingId] = useState(null);
  const [actionError, setActionError] = useState('');

  const clearBusinessData = useCallback(() => setState({ status: 'loading', plans: null, message: '' }), []);

  const load = useCallback(() => {
    let active = true;
    setState({ status: 'loading', plans: null, message: '' });

    api.listPlans()
      .then((data) => {
        if (!active) return;
        setState({ status: 'ready', plans: (data && data.plans) || [], message: '' });
      })
      .catch((error) => {
        if (!active) return;
        const outcome = handleApiError(error, { clearBusinessData });
        if (outcome.handled) return;
        setState({ status: 'error', plans: null, message: errorMessageFor(error) });
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, handleApiError]);

  useEffect(() => load(), [load]);

  // Used after a mutation succeeds (create/edit/toggle): re-fetches the
  // catalog WITHOUT flipping `status` back to 'loading' first, so the
  // always-visible create form and the rest of the page never flash away —
  // only the table's own rows swap in once the fresh GET /plans resolves. A
  // failure here (the mutation itself already succeeded) falls back to the
  // same error state `load` uses, since without a working catalog read there
  // is nothing reliable left to render.
  const reload = useCallback(() => api.listPlans()
    .then((data) => setState({ status: 'ready', plans: (data && data.plans) || [], message: '' }))
    .catch((error) => {
      const outcome = handleApiError(error, { clearBusinessData });
      if (outcome.handled) return;
      setState({ status: 'error', plans: null, message: errorMessageFor(error) });
    }), [api, handleApiError, clearBusinessData]);

  async function handleCreateSubmit(event) {
    event.preventDefault();
    if (createSubmitting) return;
    setCreateError('');
    setCreateSubmitting(true);
    try {
      await api.createPlan({ name: createValues.name.trim(), price: createValues.price.trim() });
      setCreateValues(EMPTY_VALUES);
      setCreateOpen(false);
      reload();
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData });
      if (!outcome.handled) setCreateError(errorMessageFor(caught));
    } finally {
      setCreateSubmitting(false);
    }
  }

  function startEdit(plan) {
    setEditingId(plan.id);
    setEditValues(toEditValues(plan));
    setEditError('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditError('');
  }

  async function handleEditSubmit(event) {
    event.preventDefault();
    if (editSubmitting) return;
    setEditError('');
    setEditSubmitting(true);
    try {
      await api.updatePlan(editingId, { name: editValues.name.trim(), price: editValues.price.trim() });
      setEditingId(null);
      reload();
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData });
      if (!outcome.handled) setEditError(errorMessageFor(caught));
    } finally {
      setEditSubmitting(false);
    }
  }

  function askDeactivate(plan) {
    setConfirmDeactivateId(plan.id);
    setActionError('');
  }

  function cancelDeactivate() {
    setConfirmDeactivateId(null);
  }

  async function setActive(plan, isActive) {
    setActionError('');
    setActionSubmittingId(plan.id);
    try {
      await api.updatePlan(plan.id, { isActive });
      setConfirmDeactivateId(null);
      reload();
    } catch (caught) {
      const outcome = handleApiError(caught, { clearBusinessData });
      if (!outcome.handled) setActionError(errorMessageFor(caught));
    } finally {
      setActionSubmittingId(null);
    }
  }

  if (state.status === 'loading') return <PageState title={TITLE} status="loading" />;
  if (state.status === 'error') {
    return (
      <PageState
        title={TITLE}
        status="error"
        message={state.message}
        action={<button type="button" className="gym-button gym-button--secondary" onClick={load}>Reintentar</button>}
      />
    );
  }

  const { plans } = state;

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-page__title">{TITLE}</h1>
      </header>

      <div className="gym-plans-create-trigger">
        <button type="button" className="gym-button gym-button--primary gym-plans-new" onClick={() => setCreateOpen(true)}>Nuevo plan</button>
      </div>
      <p className="gym-plans-note">{EXPLANATION}</p>

      {actionError && !confirmDeactivateId && <p role="alert" className="gym-form__error">{actionError}</p>}

      {plans.length === 0 ? (
        <p className="gym-muted-text">Todavía no hay planes cargados.</p>
      ) : (
        <table className="gym-table gym-plans-table">
          <caption className="gym-sr-only">Catálogo de planes</caption>
          <thead>
            <tr>
              <th scope="col">Nombre</th>
              <th scope="col">Precio</th>
              <th scope="col">Estado</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => {
              const submittingThis = actionSubmittingId === plan.id;
              return (
                <tr key={plan.id}>
                  <td data-label="Nombre">{plan.name}</td>
                  <td data-label="Precio">{formatMoney(plan.price)}</td>
                  <td data-label="Estado">
                    <span className={plan.isActive ? 'gym-badge gym-badge--success' : 'gym-badge gym-badge--neutral'}>
                      {plan.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td data-label="Acciones">
                    <div className="gym-plans-actions">
                      <button type="button" className="gym-button gym-button--secondary" onClick={() => startEdit(plan)}>
                        Editar
                      </button>
                      {plan.isActive ? (
                          <button type="button" className="gym-button gym-button--danger" onClick={() => askDeactivate(plan)}>
                            Desactivar
                          </button>
                      ) : (
                        <button
                          type="button"
                          className="gym-button gym-button--secondary"
                          disabled={submittingThis}
                          onClick={() => setActive(plan, true)}
                        >
                          {submittingThis ? 'Activando…' : 'Activar'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {createOpen && <PlanSheet>
        <h2 className="gym-card__title">Nuevo plan</h2>
        <PlanForm
          formLabel="Crear plan"
          idPrefix="plan-create"
          values={createValues}
          onChange={(field, value) => setCreateValues((prev) => ({ ...prev, [field]: value }))}
          onSubmit={handleCreateSubmit}
          submitting={createSubmitting}
          error={createError}
          submitLabel="Crear plan"
          submittingLabel="Creando…"
          onCancel={() => setCreateOpen(false)}
        />
      </PlanSheet>}
      {editingId && (() => {
        const plan = plans.find((item) => item.id === editingId);
        return plan && <PlanSheet>
          <h2 className="gym-card__title">Editar plan</h2>
          <PlanForm
            formLabel={`Editar ${plan.name}`}
            idPrefix={`plan-edit-${plan.id}`}
            values={editValues}
            onChange={(field, value) => setEditValues((prev) => ({ ...prev, [field]: value }))}
            onSubmit={handleEditSubmit}
            submitting={editSubmitting}
            error={editError}
            submitLabel="Guardar cambios"
            submittingLabel="Guardando…"
            onCancel={cancelEdit}
          />
        </PlanSheet>;
      })()}
      {confirmDeactivateId && (() => {
        const plan = plans.find((item) => item.id === confirmDeactivateId);
        return plan && <ConfirmationSheet
          title="¿Desactivar este plan?"
          confirmLabel="Sí, desactivar"
          pendingLabel="Desactivando…"
          busy={actionSubmittingId === plan.id}
          onConfirm={() => setActive(plan, false)}
          onClose={cancelDeactivate}
          error={actionError}
        />;
      })()}
    </section>
  );
}
